import React, {useState, useEffect} from 'react';
import {
  Box,
  Table,
  Thead,
  Tr,
  Td,
  IconButton,
  Text,
  Tbody,
  Button,
  useDisclosure,
  Modal,
  ModalBody,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Select,
  ModalCloseButton,
  Tfoot,
  ModalFooter,
  SimpleGrid,
  Flex,
  Heading,
  Checkbox,
  RadioGroup,
  Radio,
} from '@chakra-ui/react';
import FlexCard from '../../../General/FlexCard';
import BoxCard from '../../../General/BoxCard';
import {CustomCheckbox, CustomInput} from '../../../General/FormInputs';
import {FaPlusSquare, FaEdit, FaTrash} from 'react-icons/fa';
import {post, get, postDelete} from '../../../../service/ApiService';
import {OpportunityPlan} from '../../../../service/Urls';
import {getServiceType, getServiceTypes} from '../../../../helpers/ServiceTypeDictionary';
import LoadingWheel from '../../../General/LoadingWheel';
import {isAdmin} from '../../../../helpers/localStorage';

const ServiceTypes = getServiceTypes();

const Questions = ({
  questions,
  setQuestions,
  register,
  control,
  errors,
  client,
  employee,
  setToast,
  setQuestion,
  question,
  getValues,
  setValue,
  handleSubmit,
}) => {
  const {isOpen: addIsOpen, onOpen: addOnOpen, onClose: addOnClose} = useDisclosure();
  const {isOpen: editIsOpen, onOpen: editOnOpen, onClose: editOnClose} = useDisclosure();
  const [questionsToAdd, setQuestionsToAdd] = useState([]);
  const [addLoading, setAddLoading] = useState(false);
  const [selectedQuestionId, setSelectedQuestion] = useState(''); // for adding new questions, set this to which one we are adding
  const [locations, setLocations] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [unselectedLocations, setUnselectedLocations] = useState([]);
  const [addServiceType, setAddServiceType] = useState(ServiceTypes[0].value);
  const [group, setGroup] = useState({m2Grpa: false, m2Grpb: false, m2Grpc: false, m2Grpd: false, m2Grpe: false, m2Grpf: false});

  const setGroupTrue = group => {
    const groups = {m2Grpa: false, m2Grpb: false, m2Grpc: false, m2Grpd: false, m2Grpe: false, m2Grpf: false};

    const existingGroup = questions?.find(q => q[group] === true);
    if (existingGroup) {
      setValue('m2B2l', existingGroup.m2B2l);
      setValue('m2B2u', existingGroup.m2B2u);
      setValue('m2B2opp', existingGroup.m2B2opp);
      setValue('m2B3l', existingGroup.m2B3l);
      setValue('m2B3u', existingGroup.m2B3u);
      setValue('m2B3opp', existingGroup.m2B3opp);
      setValue('m2B4l', existingGroup.m2B4l);
      setValue('m2B4u', existingGroup.m2B4u);
      setValue('m2B4opp', existingGroup.m2B4opp);
    }

    groups[group] = true;
    setGroup(groups);
  };

  const LocationAction = Object.freeze({
    ADD: 1,
    REMOVE: -1,
  });

  const saveQuestion = async data => {
    if (!validateForm(data)) {
      setToast({response: {error: true}, errorMessage: 'Bracket values must be numbers'});
      return;
    }
    data = mapValues(data);

    data.clientId = client.opp.id;
    const response = await post(OpportunityPlan.UPDATE_QUESTION + '/' + question.id, data);
    setToast({response: response, successMessage: 'Question updated successfully'});
    if (response.isSuccess) {
      editOnClose();
      refreshQuestions();
    }
  };

  const addQuestion = async () => {
    setAddLoading(true);
    const selectedQuestion = questionsToAdd.find(q => q.id === selectedQuestionId);
    const data = {
      employeeId: employee.id,
      questionId: selectedQuestionId,
      clientId: client.opp.id,
      m2Quest: selectedQuestion.qmNumber,
      m2Surtype: addServiceType,
    };

    const retVal = await post(OpportunityPlan.ADD_QUESTION, data);
    setToast(retVal);
    addOnClose();

    // refresh question list
    if (retVal.isSuccess) {
      await refreshQuestions();
    }
    setAddLoading(false);
  };

  const refreshQuestions = async () => {
    const questionResponse = await get(OpportunityPlan.BONUSES + '/' + employee.id);
    setQuestions(questionResponse.data);
  };

  const addRemoveLocation = async (action, location) => {
    let url = OpportunityPlan.ADD_LOCATION;
    const data = {
      empM02Id: question.id,
      locationCode: location.id,
    };

    if (action === LocationAction.REMOVE) {
      url = OpportunityPlan.DELETE_LOCATION;
      data.locationCode = location.loId;
      setToast(await postDelete(url, data));
    } else if (action === LocationAction.ADD) {
      setToast(await post(url, data));
    }
    getLocations(question.id);
  };

  const editQuestion = async question => {
    if (question !== null) {
      setQuestion(question);
      setGroup({
        m2Grpa: question.m2Grpa,
        m2Grpb: question.m2Grpb,
        m2Grpc: question.m2Grpc,
        m2Grpd: question.m2Grpd,
        m2Grpe: question.m2Grpe,
        m2Grpf: question.m2Grpf,
      });
      getLocations(question.id);
      editOnOpen();
    }
  };

  const deleteQuestion = async question => {
    const url = OpportunityPlan.DELETE_QUESTION + '/' + question.id;
    const retVal = await postDelete(url, {});
    setToast(retVal);
    if (retVal.isSuccess) {
      await refreshQuestions();
    }
  };

  const getLocations = async questionId => {
    const locations = await get(OpportunityPlan.GET_LOCATIONS + '/' + client.opp.id + '/' + questionId);
    setSelectedLocations(locations.data.filter(l => l.isSelected));
    setUnselectedLocations(locations.data.filter(l => l.isSelected === false));
    setLocations(locations.data);
  };

  // need to convert some values to the correct type before sending to the server
  const mapValues = values => {
    return {
      employeeId: values.employeeId,
      clientId: client.opp.id,
      questionId: values.questionId,
      // "m2Client": parseFloat(values?.m2Client),
      // "m2Code": parseFloat(values?.m2Code),
      // "m2Seq": parseFloat(values?.m2Seq),
      // "m2Rectype": values?.m2Rectype,
      // "m2Quest":  values?.m2Quest,
      m2Indiv: values?.m2Indiv ?? false,
      m2B2l: parseFloat(values?.m2B2l) != NaN ? parseFloat(values?.m2B2l) : 0,
      m2B2u: parseFloat(values?.m2B2u) != NaN ? parseFloat(values?.m2B2u) : 0,
      m2B2opp: parseFloat(values?.m2B2opp) != NaN ? parseFloat(values?.m2B2opp) : 0,
      m2B3l: parseFloat(values?.m2B3l) != NaN ? parseFloat(values?.m2B3l) : 0,
      m2B3u: parseFloat(values?.m2B3u) != NaN ? parseFloat(values?.m2B3u) : 0,
      m2B3opp: parseFloat(values?.m2B3opp) != NaN ? parseFloat(values?.m2B3opp) : 0,
      m2B4l: parseFloat(values?.m2B4l) != NaN ? parseFloat(values?.m2B4l) : 0,
      m2B4u: parseFloat(values?.m2B4u) != NaN ? parseFloat(values?.m2B4u) : 0,
      m2B4opp: parseFloat(values?.m2B4opp) != NaN ? parseFloat(values?.m2B4opp) : 0,
      m2Peropp: values?.m2Peropp ?? false,
      m2Costper: parseFloat(values?.m2Costper) != NaN ? parseFloat(values?.m2Costper) : 0,
      m2Revcal: values?.m2Revcal ?? false,
      m2Locar: values?.m2Locar ?? false,
      m2Grpa: group?.m2Grpa ?? false,
      m2Grpb: group?.m2Grpb ?? false,
      m2Grpc: group?.m2Grpc ?? false,
      m2Grpd: group?.m2Grpd ?? false,
      m2Grpe: group?.m2Grpe ?? false,
      m2Grpf: group?.m2Grpf ?? false,
      m2Mgr: values?.m2Mgr ?? false,
      m2Rptdesc: values?.m2Rptdesc,
      m2Dept: values?.m2Dept,
      // m2Active: values?.m2Active,
    };
  };

  const validateNumber = value => {
    if (value === undefined || value === null || value === '') {
      return true;
    } else {
      const rgx = /^[0-9]*\.?[0-9]*$/;
      return value.toString().match(rgx);
    }
  };

  const validateForm = values => {
    if (!validateNumber(values.m2B2l)) return false;
    if (!validateNumber(values.m2B2u)) return false;
    if (!validateNumber(values.m2B2opp)) return false;
    if (!validateNumber(values.m2B3l)) return false;
    if (!validateNumber(values.m2B3u)) return false;
    if (!validateNumber(values.m2B3opp)) return false;
    if (!validateNumber(values.m2B4l)) return false;
    if (!validateNumber(values.m2B4u)) return false;
    if (!validateNumber(values.m2B4opp)) return false;
    if (!validateNumber(values.m2Costper)) return false;
    return true;
  };

  useEffect(() => {
    (async () => {
      console.log(client?.opp, employee);
      if (client?.opp && employee) {
        const questionsToAdd = await get(OpportunityPlan.GET_QUESTIONS_TO_ADD + '/' + addServiceType);
        setQuestionsToAdd(questionsToAdd.data);
        setSelectedQuestion(questionsToAdd.data[0].id);
      } else {
        setToast({response: {error: true}, errorMessage: 'Please select a client and employee first.'});
      }
    })();
  }, [employee, client, addServiceType]);

  return (
    <>
      <Box border="1px" borderRadius={'md'} mt="3">
        <Table variant={'striped'} colorScheme={'mint'}>
          <Thead>
            <Tr>
              <Td>Question</Td>
              <Td>Description</Td>
              <Td>Individual</Td>
              <Td>Type</Td>
              <Td>Group</Td>

              <Td>
                {isAdmin() && (
                  <IconButton
                    title="Add"
                    colorScheme="secondary"
                    variant={'outline'}
                    size="md"
                    mt="-1.5"
                    icon={<FaPlusSquare />}
                    onClick={() => {
                      if (employee && client) addOnOpen();
                    }}
                  />
                )}
              </Td>
            </Tr>
          </Thead>
          <Tbody>
            {questions?.map(q => {
              return (
                <Tr key={q.id}>
                  <Td>{q.m2Quest}</Td>
                  <Td>{q.qmDesc}</Td>
                  <Td>{q.m2Indiv ? 'Yes' : 'No'}</Td>
                  <Td>{getServiceType(q.m2Surtype)?.key ?? '-'}</Td>
                  <Td>{q.groupCode}</Td>
                  <Td>
                    <IconButton
                      title="Edit"
                      colorScheme="secondary"
                      variant={'outline'}
                      size="md"
                      mr="4"
                      mt="-1.5"
                      icon={<FaEdit />}
                      onClick={() => {
                        editQuestion(q);
                      }}
                    />
                    {isAdmin() && (
                      <IconButton
                        title="Delete"
                        colorScheme="red"
                        variant={'outline'}
                        size="md"
                        mt="-1.5"
                        icon={<FaTrash />}
                        onClick={() => {
                          deleteQuestion(q);
                        }}
                      />
                    )}
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
          <Tfoot></Tfoot>
        </Table>
      </Box>

      {/** Modal for Adding **/}
      <Modal isOpen={addIsOpen} onClose={addOnClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader> Select question to add</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Box rounded="lg" boxShadow="lg" border="1px" p="2" px="30" mb={2} borderColor={'tertiary.200'}>
              <Text fontWeight="bold" fontSize="2xl">
                Opportunity Plan Type
              </Text>
              <RadioGroup value={addServiceType} onChange={e => setAddServiceType(parseInt(e))}>
                <Flex direction={'column'}>
                  {ServiceTypes.map((type, index) => {
                    return (
                      <Radio key={index} value={type.value}>
                        {type.key}
                      </Radio>
                    );
                  })}
                </Flex>
              </RadioGroup>
            </Box>
            <FlexCard mb={2}>
              <Select w="100%" name="Select Question" onChange={e => setSelectedQuestion(e.target.value)}>
                {questionsToAdd == [] || questionsToAdd === undefined
                  ? []
                  : questionsToAdd.map(qu => {
                      return (
                        <option key={qu.id} value={qu.id}>
                          {qu.qmNumber + ' | ' + qu.qmDesc}
                        </option>
                      );
                    })}
              </Select>
            </FlexCard>
            {addLoading ? (
              <Flex justify={'center'}>
                <LoadingWheel />
              </Flex>
            ) : (
              <Button type="button" colorScheme={'secondary'} onClick={addQuestion} w="100%">
                Add
              </Button>
            )}
          </ModalBody>
          <ModalFooter></ModalFooter>
        </ModalContent>
      </Modal>

      {/** Modal for Editing **/}
      <Modal isOpen={editIsOpen} onClose={editOnClose}>
        <ModalOverlay />
        <ModalContent maxW="70%">
          <ModalHeader> {`Edit Question: ${question?.m2Quest} | ${question?.qmDesc}`}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <BoxCard>
              <Flex w="100%" mb="3">
                <Box boxShadow="lg" rounded="lg" border="1px" p="2" borderColor={'tertiary.200'}>
                  <Box>
                    <SimpleGrid columns={7} mb="3">
                      <Text />
                      <CustomCheckbox w="100%" control={control} id="m2Peropp" name="%" />
                      <Text />
                      <Heading size="md">Between</Heading>
                      <Text />
                      <Text />
                      <Heading size="md">Qtr Opp</Heading>
                    </SimpleGrid>
                    <SimpleGrid columns={4}>
                      <Text>Bracket 2</Text>
                      <CustomInput register={register} errors={errors} id="m2B2l" />
                      <CustomInput register={register} errors={errors} id="m2B2u" />
                      <CustomInput register={register} errors={errors} id="m2B2opp" />
                      <Text>Bracket 5</Text>
                      <CustomInput register={register} errors={errors} id="m2B3l" />
                      <CustomInput register={register} errors={errors} id="m2B3u" />
                      <CustomInput register={register} errors={errors} id="m2B3opp" />
                      <Text>Bracket 4</Text>
                      <CustomInput register={register} errors={errors} id="m2B4l" />
                      <CustomInput register={register} errors={errors} id="m2B4u" />
                      <CustomInput register={register} errors={errors} id="m2B4opp" />
                    </SimpleGrid>
                  </Box>
                  <CustomInput register={register} id="m2Costper" name="Cost Factor Percent" errors={errors} isReadOnly={employee ? false : true} />
                </Box>
              </Flex>
              <Flex>
                <CustomCheckbox w="100%" control={control} id="m2Indiv" name="Individual" />
                <Checkbox
                  w="100%"
                  colorScheme={'tertiary'}
                  isChecked={group.m2Grpa}
                  onChange={() => {
                    setGroupTrue('m2Grpa');
                  }}
                >
                  Group A
                </Checkbox>
                <Checkbox
                  w="100%"
                  colorScheme={'tertiary'}
                  isChecked={group.m2Grpc}
                  onChange={() => {
                    setGroupTrue('m2Grpc');
                  }}
                >
                  Group C
                </Checkbox>
                <Checkbox
                  w="100%"
                  colorScheme={'tertiary'}
                  isChecked={group.m2Grpe}
                  onChange={() => {
                    setGroupTrue('m2Grpe');
                  }}
                >
                  Group E
                </Checkbox>
                <CustomCheckbox w="100%" control={control} id="m2Revcal" name="Calculate on Revenue" />
              </Flex>
              <Flex mb="3">
                <CustomCheckbox w="100%" control={control} id="m2Mgr" name="Manager" />
                <Checkbox
                  w="100%"
                  colorScheme={'tertiary'}
                  isChecked={group.m2Grpb}
                  onChange={() => {
                    setGroupTrue('m2Grpb');
                  }}
                >
                  Group B
                </Checkbox>
                <Checkbox
                  w="100%"
                  colorScheme={'tertiary'}
                  isChecked={group.m2Grpd}
                  onChange={() => {
                    setGroupTrue('m2Grpd');
                  }}
                >
                  Group D
                </Checkbox>
                <Checkbox
                  w="100%"
                  colorScheme={'tertiary'}
                  isChecked={group.m2Grpf}
                  onChange={() => {
                    setGroupTrue('m2Grpf');
                  }}
                >
                  Group F
                </Checkbox>
                <CustomCheckbox w="100%" control={control} id="m2Locar" name="Use Location A/R" />
              </Flex>
              <CustomInput register={register} id="m2Rptdesc" name="Report Desc" errors={errors} isReadOnly={employee ? false : true} />

              {isAdmin() && (
                <Flex mt="3">
                  <Button type="button" colorScheme={'secondary'} onClick={handleSubmit(saveQuestion)} w="100%" mr="2">
                    Save
                  </Button>
                </Flex>
              )}
            </BoxCard>

            <BoxCard>
              <Flex>
                <Box w="100%" border="1px" borderRadius="md" borderColor="tertiary.200" mr="2">
                  <SimpleGrid columns={3} w="100%" p="5">
                    <Heading size="sm">Id</Heading>
                    <Heading size="sm">Location</Heading>
                    <Heading size="sm">Select</Heading>
                    {unselectedLocations.map(loc => {
                      return (
                        <React.Fragment key={loc.id}>
                          <Text>{loc.loId}</Text>
                          <Text>{loc.loOppname}</Text>
                          {isAdmin() ? (
                            <Button type="button" colorScheme={'secondary'} w="50%" mb="2" onClick={() => addRemoveLocation(LocationAction.ADD, loc)}>
                              Select
                            </Button>
                          ) : (
                            <Text> </Text>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </SimpleGrid>
                </Box>
                <Box w="100%" border="1px" borderRadius="md" borderColor="tertiary.200">
                  <SimpleGrid columns={3} w="100%" p="5">
                    <Heading size="sm">Id</Heading>
                    <Heading size="sm">Location</Heading>
                    <Heading size="sm">Unselect</Heading>
                    {selectedLocations.map(loc => {
                      return (
                        <React.Fragment key={loc.id}>
                          <Text>{loc.loId}</Text>
                          <Text>{loc.loOppname}</Text>
                          {isAdmin() ? (
                            <Button
                              type="button"
                              colorScheme={'secondary'}
                              w="50%"
                              mb="2"
                              onClick={() => addRemoveLocation(LocationAction.REMOVE, loc)}
                            >
                              Unselect
                            </Button>
                          ) : (
                            <Text> </Text>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </SimpleGrid>
                </Box>
              </Flex>
            </BoxCard>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default Questions;
